// Sugalarm — slim one-line glance pill (cycle 115).
//
// Landscape replaces the tall mobile header. Household + Settings show
// this ~24px pill top-right: current value + trend arrow + age + CGM
// source (e.g. `129 → · 2m · Dexcom`). Reuses the existing hero /
// staleness plumbing (displayValue / trendInfo / fmtAge / zoneColor /
// ConnDot) — no new math. Collapses to a "— No Data" treatment when the
// reading is null or stale, mirroring the header.

import type { DisplayGlucose } from "../../lib/currentGlucose";
import { displayValue, fmtAge, trendInfo, zoneColor } from "../../lib/glucose";
import type { Zone } from "../../lib/types";
import { TrendGlyphSvg } from "../glucose/TrendGlyphSvg";
import { ConnDot } from "./ConnDot";

interface Props {
  /** ⚠ Cycle 435: a selector-released `DisplayGlucose`, or null — which is
   *  the "— No Data" treatment. A raw `Reading` cannot be passed here. */
  current: DisplayGlucose | null;
  units: "mg/dL" | "mmol/L";
  ageMin: number;
  zone: Zone;
  /** The provider to name when there is no current value (the member's
   *  connected CGM), so No Data still says which feed is dark. */
  provider?: string | null;
}

export function GlancePill({ current, units, ageMin, zone, provider: providerIn }: Props) {
  const noData = current == null;
  const heroColor = noData ? "var(--ink-3)" : zoneColor(zone);
  const providerName = current?.provider ?? providerIn ?? null;
  const provider = providerName
    ? providerName[0]!.toUpperCase() + providerName.slice(1)
    : "Sugar Alarm";
  const dot = (
    <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-4)" }} aria-hidden>
      ·
    </span>
  );
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 24,
        padding: "0 10px",
        borderRadius: 999,
        background: "var(--bg-elev)",
        border: "0.5px solid var(--line)",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {noData ? (
        <>
          <span
            className="tnum"
            style={{
              fontFamily: "var(--hero-font)",
              fontWeight: 600,
              fontSize: 14,
              color: "var(--zone-urgent)",
            }}
          >
            —
          </span>
          <span
            style={{
              fontSize: "var(--type-floor)",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--zone-urgent)",
            }}
          >
            No Data
          </span>
          {dot}
          <ConnDot status="stale" />
          <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>{provider}</span>
        </>
      ) : (
        <>
          <span
            className="tnum"
            style={{
              fontFamily: "var(--hero-font)",
              fontWeight: 600,
              fontSize: 14,
              color: heroColor,
              letterSpacing: "-0.02em",
            }}
          >
            {displayValue(current.sgv, units)}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              color: heroColor,
            }}
            aria-hidden
          >
            <TrendGlyphSvg
              glyph={trendInfo(current.trend, current.trendRate).glyph}
              size={15}
            />
          </span>
          {dot}
          <span className="tnum" style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>
            {fmtAge(ageMin)}
          </span>
          {dot}
          <ConnDot status="ok" />
          <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>{provider}</span>
        </>
      )}
    </div>
  );
}
